export function safeString(v) {
  return typeof v === "string" ? v : v == null ? "" : String(v);
}

export function safeDate(input) {
  if (!input) return null;
  const d = new Date(input);
  return Number.isNaN(d.getTime()) ? null : d;
}

export function formatWhen(input) {
  const d = safeDate(input);
  if (!d) return "";
  return d.toLocaleString([], {
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function timeValue(input) {
  const d = safeDate(input);
  return d ? d.getTime() : null;
}

function normalizeLineEndings(value) {
  return safeString(value).replace(/\r\n/g, "\n").replace(/\r/g, "\n");
}

function isLikelyDateText(value) {
  const text = safeString(value).trim();
  if (!text) return false;

  const d = safeDate(text);
  if (d) return true;

  return (
    /\d{1,2}\/\d{1,2}\/\d{2,4}/.test(text) ||
    /\d{4}-\d{2}-\d{2}/.test(text)
  );
}

function stripRoleSuffix(value) {
  const raw = safeString(value).trim();
  if (!raw) return "";

  return raw
    .replace(/\s*\((admin|manager|service manager|advisor|service advisor|technician|tech|parts|parts advisor|parts manager|foreman|shop foreman|dispatcher|staff|user|support|super admin)\)\s*$/i, "")
    .trim();
}

export function cleanAuthorName(value) {
  const raw = stripRoleSuffix(value);
  if (!raw) return "User";

  const lowered = raw.toLowerCase();
  if (lowered === "unknown" || lowered === "note") return "User";

  return raw;
}

function legacyHeaderTimestamp(value) {
  const raw = safeString(value).trim();
  if (!raw) return "";

  const d = safeDate(raw);
  if (!d) return raw;

  return d.toUTCString();
}

function objectNoteText(item) {
  if (!item || typeof item !== "object") return "";

  const text = [
    item.text,
    item.body,
    item.note,
    item.message,
    item.value,
  ]
    .map((candidate) => safeString(candidate).trim())
    .find(Boolean);

  if (!text) return "";

  const timestamp = [
    item.createdAtISO,
    item.createdAt,
    item.at,
    item.timestamp,
  ]
    .map((candidate) => safeString(candidate).trim())
    .find(Boolean);

  const byName = safeString(item.byName || item.actorName).trim();
  const byRole = safeString(item.byRole || item.actorRole).trim();
  const author =
    byName && byRole ? `${byName} (${byRole})` : byName || byRole;
  const headerTimestamp = legacyHeaderTimestamp(timestamp);

  if (!headerTimestamp && !author) return text;

  const header =
    headerTimestamp && author
      ? `${headerTimestamp} \u2014 ${author}`
      : headerTimestamp
      ? `${headerTimestamp} \u2014 User`
      : author;

  return `${header}\n${text}`;
}

export function noteTextFromValue(value) {
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item;
        if (item && typeof item === "object") return objectNoteText(item);
        return safeString(item).trim();
      })
      .filter(Boolean)
      .join("\n\n");
  }

  if (value && typeof value === "object") {
    return objectNoteText(value);
  }

  const raw = safeString(value).trim();
  return raw === "[object Object]" ? "" : raw;
}

export function parseLegacyNoteHeader(header) {
  const text = safeString(header).trim();

  if (!text) {
    return {
      author: "User",
      when: "",
    };
  }

  const match = text.match(/^(.+?)\s*[-\u2013\u2014]\s*(.+)$/);

  if (!match) {
    return {
      author: cleanAuthorName(text),
      when: "",
    };
  }

  const left = safeString(match[1]).trim();
  const right = safeString(match[2]).trim();

  const leftLooksDate = isLikelyDateText(left);
  const rightLooksDate = isLikelyDateText(right);

  if (leftLooksDate && !rightLooksDate) {
    return {
      author: cleanAuthorName(right),
      when: left,
    };
  }

  if (rightLooksDate && !leftLooksDate) {
    return {
      author: cleanAuthorName(left),
      when: right,
    };
  }

  return {
    author: cleanAuthorName(right || left),
    when: leftLooksDate ? left : "",
  };
}

function epochSecond(value) {
  const d = safeDate(value);
  return d ? Math.floor(d.getTime() / 1000) : null;
}

function isRecognizedTimestampHeader(value) {
  const text = safeString(value).trim();
  if (!text) return false;
  if (!/^(.+?)\s*[-\u2013\u2014]\s*(.+)$/.test(text)) return false;

  const parsed = parseLegacyNoteHeader(text);
  return epochSecond(parsed.when) != null;
}

function hasTrailingBlank(lines) {
  return lines.length > 0 && safeString(lines[lines.length - 1]).trim() === "";
}

function blockToLegacyNote(block, idx = 0) {
  const raw = normalizeLineEndings(block).trim();
  if (!raw) return null;

  const newlineIndex = raw.indexOf("\n");
  const header =
    newlineIndex >= 0 ? raw.slice(0, newlineIndex).trim() : raw.trim();
  const body =
    newlineIndex >= 0 ? raw.slice(newlineIndex + 1).trim() : header;

  return {
    id: `${idx}-${header || "note"}`,
    header,
    body,
    raw,
    originalIndex: idx,
  };
}

export function splitLegacyNoteBlocks(notes) {
  const raw = normalizeLineEndings(noteTextFromValue(notes)).trim();
  if (!raw) return [];

  const lines = raw.split("\n");
  const hasRecognizedHeader = lines.some((line) =>
    isRecognizedTimestampHeader(line)
  );

  if (!hasRecognizedHeader) {
    return raw
      .split(/\n\s*\n/)
      .map(blockToLegacyNote)
      .filter(Boolean)
      .map((note, idx) => ({ ...note, originalIndex: idx }));
  }

  const blocks = [];
  let current = [];

  lines.forEach((line) => {
    if (
      current.length > 0 &&
      hasTrailingBlank(current) &&
      isRecognizedTimestampHeader(line)
    ) {
      blocks.push(current.join("\n").trim());
      current = [];
    }

    current.push(line);
  });

  if (current.length) {
    blocks.push(current.join("\n").trim());
  }

  return blocks
    .map(blockToLegacyNote)
    .filter(Boolean)
    .map((note, idx) => ({ ...note, originalIndex: idx }));
}

function normalizeBody(value) {
  return normalizeLineEndings(value).trim();
}

function normalizeAuthor(value) {
  return safeString(value).trim().toLowerCase();
}

function fingerprint(epochSecondValue, author, body) {
  if (epochSecondValue == null) return "";

  const normalizedAuthor = normalizeAuthor(author);
  const normalizedBody = normalizeBody(body);

  if (!normalizedAuthor || !normalizedBody) return "";
  return `${epochSecondValue}\n${normalizedAuthor}\n${normalizedBody}`;
}

function messageFingerprint(message) {
  return fingerprint(
    epochSecond(message?.createdAt),
    message?.authorDisplayName,
    message?.body
  );
}

function legacyFingerprint(note) {
  const parsed = parseLegacyNoteHeader(note?.header);
  return fingerprint(epochSecond(parsed.when), parsed.author, note?.body);
}

function retractedMessageLegacyKey(message) {
  if (message?.isRetracted !== true) return "";

  const createdSecond = epochSecond(message?.createdAt);
  const author = normalizeAuthor(message?.authorDisplayName);
  if (createdSecond == null || !author) return "";

  return `${createdSecond}\n${author}`;
}

function legacyAuthorTimeKey(note) {
  const parsed = parseLegacyNoteHeader(note?.header);
  const createdSecond = epochSecond(parsed.when);
  const author = normalizeAuthor(parsed.author);
  const body = normalizeBody(note?.body);
  if (createdSecond == null || !author || !body) return "";

  return `${createdSecond}\n${author}`;
}

export function filterLegacyNotesForStructuredMessages(notes, messages) {
  const structuredCounts = new Map();
  const retractedCounts = new Map();

  (Array.isArray(messages) ? messages : []).forEach((message) => {
    const key = messageFingerprint(message);
    if (key) {
      structuredCounts.set(key, (structuredCounts.get(key) || 0) + 1);
    }

    const retractedKey = retractedMessageLegacyKey(message);
    if (retractedKey) {
      retractedCounts.set(
        retractedKey,
        (retractedCounts.get(retractedKey) || 0) + 1
      );
    }
  });

  return (Array.isArray(notes) ? notes : []).filter((note) => {
    const key = legacyFingerprint(note);

    if (key) {
      const count = structuredCounts.get(key) || 0;
      if (count > 0) {
        if (count === 1) structuredCounts.delete(key);
        else structuredCounts.set(key, count - 1);

        return false;
      }
    }

    const retractedKey = legacyAuthorTimeKey(note);
    if (!retractedKey) return true;

    const retractedCount = retractedCounts.get(retractedKey) || 0;
    if (retractedCount <= 0) return true;

    if (retractedCount === 1) retractedCounts.delete(retractedKey);
    else retractedCounts.set(retractedKey, retractedCount - 1);

    return false;
  });
}
